/**
 * SoloSingleFileAudioRecording - Records audio directly to a single WebM/Opus file.
 * 
 * Key features:
 * - Uses MediaBunny's StreamTarget to write directly to OPFS as recording happens
 * - File is complete and ready for playback immediately when recording stops
 * - No chunking, no stitching - just a single file
 * - Uses same upload flow as user file uploads (UploadAsset with isRecording=true)
 * - Opus codec is excellent for speech/voice recording
 */

import {
    Output,
    WebMOutputFormat,
    AudioBufferSource,
    StreamTarget,
    StreamTargetChunk,
} from 'mediabunny';

// Recording options
export interface SoloSingleFileAudioRecordingOptions {
    trackId: string;
    deviceId?: string;              // specific microphone device ID
    stream?: MediaStream;           // optional pre-processed stream
    title?: string;
    projectId?: string;
    sampleRate?: number;            // sample rate (default: 48000)
    autoGainControl?: boolean;      // enable auto gain control (default: true)
    noiseSuppression?: boolean;     // enable browser noise suppression (default: true)
}

// Recording state
type RecordingState = 'idle' | 'recording' | 'stopping' | 'stopped';

const DEFAULT_SAMPLE_RATE = 48000;

// OPFS path for solo recordings (WebM/Opus audio)
const soloRecordingPath = (trackId: string): string => 
    `solo-recordings/${trackId}.webm`;

/**
 * Get the OPFS root directory.
 */
async function getOpfsRoot(): Promise<FileSystemDirectoryHandle> {
    return await navigator.storage.getDirectory();
}

/**
 * Create a file in OPFS and return a writable stream.
 */
async function createOpfsWritableFile(path: string): Promise<{
    fileHandle: FileSystemFileHandle;
    writable: FileSystemWritableFileStream;
}> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;
    
    let dir = await getOpfsRoot();
    for (const segment of parts) {
        dir = await dir.getDirectoryHandle(segment, { create: true });
    }
    
    const fileHandle = await dir.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    
    return { fileHandle, writable };
}

/**
 * Read a file from OPFS and ensure it has the correct MIME type.
 */
async function readOpfsFile(path: string): Promise<File> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;
    
    let dir = await getOpfsRoot();
    for (const segment of parts) {
        dir = await dir.getDirectoryHandle(segment, { create: false });
    }
    
    const fileHandle = await dir.getFileHandle(fileName, { create: false });
    const file = await fileHandle.getFile();
    
    // OPFS files may not have correct MIME type - ensure it's set for WebM audio
    if (!file.type || file.type === 'application/octet-stream') {
        const blob = await file.arrayBuffer();
        return new File([blob], fileName, { type: 'audio/webm' });
    }
    
    return file;
}

/**
 * Delete a file from OPFS.
 */
async function deleteOpfsFile(path: string): Promise<void> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;
    
    let dir = await getOpfsRoot();
    for (const segment of parts) {
        try {
            dir = await dir.getDirectoryHandle(segment, { create: false });
        } catch {
            return; // Directory doesn't exist, nothing to delete
        }
    }
    
    try {
        await dir.removeEntry(fileName);
    } catch {
        // File doesn't exist, that's fine
    }
}

export class SoloSingleFileAudioRecording {
    private trackId: string;
    private deviceId?: string;
    private providedStream?: MediaStream;  // Pre-processed stream
    private title?: string;
    private projectId?: string;
    private sampleRate: number;
    private autoGainControl: boolean;
    private noiseSuppression: boolean;

    private state: RecordingState = 'idle';
    private stream: MediaStream | null = null;
    private ownsStream: boolean = false;  // Whether we created the stream (and should stop it)
    private audioContext: AudioContext | null = null;
    private scriptProcessor: ScriptProcessorNode | null = null;
    private sourceNode: MediaStreamAudioSourceNode | null = null;

    // MediaBunny encoding
    private output: Output | null = null;
    private audioSource: AudioBufferSource | null = null;
    private writable: FileSystemWritableFileStream | null = null;
    private opfsPath: string;
    
    // Audio sample queue for batching
    private sampleQueue: Float32Array[] = [];
    private paused: boolean = false;

    constructor(options: SoloSingleFileAudioRecordingOptions) {
        this.trackId = options.trackId;
        this.deviceId = options.deviceId;
        this.providedStream = options.stream;
        this.title = options.title;
        this.projectId = options.projectId;
        this.sampleRate = options.sampleRate ?? DEFAULT_SAMPLE_RATE;
        this.autoGainControl = options.autoGainControl ?? true;
        this.noiseSuppression = options.noiseSuppression ?? true;
        this.opfsPath = soloRecordingPath(this.trackId);
    }

    /**
     * Start recording audio from the microphone.
     */
    public async start(): Promise<void> {
        if (this.state !== 'idle') {
            console.warn('[SoloSingleFileAudioRecording] Cannot start, already recording');
            return;
        }

        console.log('[SoloSingleFileAudioRecording] Starting recording to:', this.opfsPath);

        // 1. Create OPFS file and get writable stream
        const { writable } = await createOpfsWritableFile(this.opfsPath);
        this.writable = writable;

        // 2. Create MediaBunny output with StreamTarget (WebM format)
        const streamTarget = new StreamTarget(
            writable as unknown as WritableStream<StreamTargetChunk>,
            { chunked: true }
        );
        this.output = new Output({ format: new WebMOutputFormat(), target: streamTarget });

        // 3. Create audio source for Opus encoding (excellent for speech)
        this.audioSource = new AudioBufferSource({
            codec: 'opus',
            bitrate: 128_000, // 128 kbps - high quality for speech
        });
        this.output.addAudioTrack(this.audioSource);

        // 4. Start the encoder
        await this.output.start();

        // 5. Get audio stream - use provided stream or get microphone access
        if (this.providedStream) {
            // Use pre-processed stream
            this.stream = this.providedStream;
            this.ownsStream = false;
            console.log('[SoloSingleFileAudioRecording] Using provided stream');
        } else {
            // Get microphone access
            const constraints: MediaStreamConstraints = {
                audio: this.deviceId
                    ? { 
                        deviceId: { exact: this.deviceId }, 
                        sampleRate: this.sampleRate,
                        autoGainControl: this.autoGainControl,
                        echoCancellation: this.noiseSuppression,
                        noiseSuppression: this.noiseSuppression,
                    }
                    : { 
                        sampleRate: this.sampleRate,
                        autoGainControl: this.autoGainControl,
                        echoCancellation: this.noiseSuppression,
                        noiseSuppression: this.noiseSuppression,
                    },
                video: false,
            };

            this.stream = await navigator.mediaDevices.getUserMedia(constraints);
            this.ownsStream = true;
        }

        // 6. Set up audio processing
        this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
        this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);

        // Use ScriptProcessorNode to capture audio samples
        // Note: ScriptProcessorNode is deprecated but AudioWorklet requires more setup
        const bufferSize = 4096;
        this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);

        this.scriptProcessor.onaudioprocess = (event) => {
            if (this.state !== 'recording') return;
            if (this.paused) return;
            if (!this.audioSource) return;

            const inputData = event.inputBuffer.getChannelData(0);
            
            // Copy data as the buffer is reused
            const samples = new Float32Array(inputData.length);
            samples.set(inputData);
            
            // Queue samples for encoding (will be processed asynchronously)
            this.sampleQueue.push(samples);
        };
        
        // Start background encoding loop
        this.processAudioQueue();

        this.sourceNode.connect(this.scriptProcessor);
        this.scriptProcessor.connect(this.audioContext.destination);

        this.state = 'recording';
        this.paused = false;
        console.log('[SoloSingleFileAudioRecording] Recording started');
    }

    /**
     * Process queued audio samples and add to encoder.
     * Runs continuously while recording.
     */
    private async processAudioQueue(): Promise<void> {
        while (this.state === 'recording' || this.sampleQueue.length > 0) {
            // Process all queued samples
            while (this.sampleQueue.length > 0 && this.audioSource && this.audioContext) {
                const samples = this.sampleQueue.shift()!;
                
                // Convert Float32Array to AudioBuffer
                const buffer = this.audioContext.createBuffer(
                    1, // mono
                    samples.length,
                    this.sampleRate
                );
                buffer.copyToChannel(samples, 0);
                
                // Add to encoder
                try {
                    await this.audioSource.add(buffer);
                } catch (err) {
                    console.error('[SoloSingleFileAudioRecording] Error adding buffer:', err);
                }
            }
            
            // Small delay to avoid tight loop
            if (this.state === 'recording') {
                await new Promise(resolve => setTimeout(resolve, this.paused ? 200 : 10));
            }
        }
    }

    /**
     * Pause recording (best-effort). While paused, no new samples are encoded.
     */
    public async pause(): Promise<void> {
        if (this.state !== 'recording') return;
        if (this.paused) return;
        this.paused = true;
        if (this.audioContext) {
            try {
                await this.audioContext.suspend();
            } catch { }
        }
    }

    /**
     * Resume recording after a pause (best-effort).
     */
    public async resume(): Promise<void> {
        if (this.state !== 'recording') return;
        if (!this.paused) return;
        this.paused = false;
        if (this.audioContext) {
            try {
                await this.audioContext.resume();
            } catch { }
        }
    }

    /**
     * Check if recording is paused.
     */
    public isPaused(): boolean {
        return this.paused;
    }

    /**
     * Stop recording and finalize the file.
     * Returns the path to the recorded FLAC file in OPFS.
     */
    public async stop(): Promise<{ trackId: string; filePath: string; file: File }> {
        if (this.state !== 'recording') {
            console.warn('[SoloSingleFileAudioRecording] Cannot stop, not recording');
            throw new Error('Not recording');
        }

        this.state = 'stopping';
        this.paused = false;
        console.log('[SoloSingleFileAudioRecording] Stopping recording...');

        // 1. Disconnect audio processing first (stops new samples coming in)
        if (this.scriptProcessor) {
            this.scriptProcessor.onaudioprocess = null;
            this.scriptProcessor.disconnect();
            this.scriptProcessor = null;
        }
        if (this.sourceNode) {
            this.sourceNode.disconnect();
            this.sourceNode = null;
        }

        // 2. Drain remaining samples in queue (need audioContext for this)
        while (this.sampleQueue.length > 0 && this.audioSource && this.audioContext) {
            const samples = this.sampleQueue.shift()!;
            const buffer = this.audioContext.createBuffer(1, samples.length, this.sampleRate);
            buffer.copyToChannel(samples, 0);
            try {
                await this.audioSource.add(buffer);
            } catch (err) {
                console.error('[SoloSingleFileAudioRecording] Error adding final buffer:', err);
            }
        }

        // 3. Finalize the MediaBunny output (this completes the FLAC file)
        if (this.output) {
            await this.output.finalize();
            this.output = null;
            this.audioSource = null;
        }

        // 4. Close audio context
        if (this.audioContext) {
            await this.audioContext.close();
            this.audioContext = null;
        }

        // 5. Stop media stream (only if we created it)
        if (this.stream && this.ownsStream) {
            this.stream.getTracks().forEach(track => track.stop());
        }
        this.stream = null;

        // 6. Close the writable stream (should be done by finalize, but be safe)
        if (this.writable) {
            try {
                await this.writable.close();
            } catch {
                // Already closed by finalize
            }
            this.writable = null;
        }

        this.state = 'stopped';

        // 7. Read the completed file
        const file = await readOpfsFile(this.opfsPath);
        
        console.log('[SoloSingleFileAudioRecording] Recording complete!',
            'File size:', file.size, 'bytes',
            'Path:', this.opfsPath);

        return {
            trackId: this.trackId,
            filePath: this.opfsPath,
            file,
        };
    }

    /**
     * Check if currently recording.
     */
    public isRecording(): boolean {
        return this.state === 'recording';
    }

    /**
     * Get the track ID.
     */
    public getTrackId(): string {
        return this.trackId;
    }

    /**
     * Get the OPFS path where the file will be saved.
     */
    public getFilePath(): string {
        return this.opfsPath;
    }

    /**
     * Clean up the recording file (e.g., if cancelled).
     */
    public async cleanup(): Promise<void> {
        // Stop if still recording
        if (this.state === 'recording') {
            try {
                await this.stop();
            } catch {
                // Force cleanup
            }
        }

        // Delete the file
        await deleteOpfsFile(this.opfsPath);
        console.log('[SoloSingleFileAudioRecording] Cleaned up:', this.opfsPath);
    }
}

/**
 * Generate a unique track ID for a solo recording.
 */
export function generateSoloTrackId(): string {
    return `solo_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// Export utility functions for external use
export { readOpfsFile, deleteOpfsFile, soloRecordingPath };
